import React, { useEffect, useState } from 'react';
import Card from 'components/card';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { MdExpandMore, MdExpandLess, MdShoppingCart } from 'react-icons/md';

interface Lead {
    _id: string;
    sellerId: {
        _id: string;
        firstName: string;
        lastName: string;
        email: string;
    };
    buyerPrice: number;
    leadPrice: number;
    email: string;
    status: string;
    isPaid?: boolean;
    createdAt: string;
}

interface PurchaseOrder {
    _id: string;
    ownerId: {
        _id: string;
        firstName: string;
        lastName: string;
        email: string;
    } | null;
    price?: number; // Deprecated field
    maxPricePerLead?: number; // New field
    leadCount: number;
    industry: string;
    product: string;
    departments: string[];
    status: 'active' | 'paused' | 'completed' | 'cancelled';
    totalBudget: number;
    leadsReceived: number;
    amountSpent: number;
    createdAt: string;
    leads: Lead[];
}

interface GroupedOrders {
    industry: string;
    product: string;
    totalVolume: number;
    totalPrice: number;
    totalLeads: number;
    orders: PurchaseOrder[];
}

export default function BackOfficePurchaseOrders() {
    const [groups, setGroups] = useState<GroupedOrders[]>([]);
    const [loading, setLoading] = useState(true);
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
    const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());
    const [totalLeads, setTotalLeads] = useState(0);

    useEffect(() => {
        fetchPurchaseOrders();
    }, []);

    const fetchPurchaseOrders = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${CONFIG.apiUrl}/admin/purchase-orders`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setGroups(response.data.groups);
            setTotalLeads(response.data.totalLeads || 0);
        } catch (error) {
            console.error('Error fetching purchase orders:', error);
        } finally {
            setLoading(false);
        }
    };

    const toggleGroup = (key: string) => {
        const newExpanded = new Set(expandedGroups);
        if (newExpanded.has(key)) {
            newExpanded.delete(key);
        } else {
            newExpanded.add(key);
        }
        setExpandedGroups(newExpanded);
    };

    const toggleOrder = (orderId: string) => {
        const newExpanded = new Set(expandedOrders);
        if (newExpanded.has(orderId)) {
            newExpanded.delete(orderId);
        } else {
            newExpanded.add(orderId);
        }
        setExpandedOrders(newExpanded);
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'active':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
            case 'paused':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
            case 'completed':
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
            case 'cancelled':
                return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
        }
    };

    return (
        <div className="pt-[50px]">
            <Card extra="p-5">
                <div className="mb-5 flex items-center justify-between gap-2">
                    <div>
                        <div className="text-2xl font-bold text-navy-700 dark:text-white">
                            Purchase Orders
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            Grouped by industry and product - Sorted by highest price
                        </div>
                        <div className="mt-2 text-sm font-semibold text-brand-500">
                            Total Leads Received: {totalLeads.toLocaleString()}
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="py-8 text-center text-gray-600 dark:text-gray-400">
                        Loading purchase orders...
                    </div>
                ) : groups.length === 0 ? (
                    <div className="py-8 text-center text-gray-600 dark:text-gray-400">
                        No purchase orders found
                    </div>
                ) : (
                    <div className="space-y-4">
                        {groups.map((group, index) => {
                            const groupKey = `${group.industry}-${group.product}`;
                            const isExpanded = expandedGroups.has(groupKey);

                            return (
                                <div
                                    key={groupKey}
                                    className="rounded-lg border border-gray-200 dark:border-navy-600"
                                >
                                    {/* Folder Header */}
                                    <div
                                        className="flex cursor-pointer items-center justify-between bg-gray-50 p-4 hover:bg-gray-100 dark:bg-navy-800 dark:hover:bg-navy-700"
                                        onClick={() => toggleGroup(groupKey)}
                                    >
                                        <div className="flex items-center gap-3">
                                            <MdShoppingCart className="text-brand-500" size={24} />
                                            <div>
                                                <div className="font-bold text-navy-700 dark:text-white">
                                                    {group.industry} - {group.product}
                                                </div>
                                                <div className="text-sm text-gray-600 dark:text-gray-400">
                                                    {group.orders.length} order{group.orders.length > 1 ? 's' : ''}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <div className="text-right">
                                                <div className="text-sm text-gray-600 dark:text-gray-400">
                                                    Leads Received
                                                </div>
                                                <div className="font-bold text-green-500">
                                                    {group.totalLeads?.toLocaleString() || 0} leads
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-sm text-gray-600 dark:text-gray-400">
                                                    Total Volume
                                                </div>
                                                <div className="font-bold text-navy-700 dark:text-white">
                                                    {group.totalVolume.toLocaleString()} leads
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-sm text-gray-600 dark:text-gray-400">
                                                    Total Budget
                                                </div>
                                                <div className="font-bold text-brand-500">
                                                    €{group.totalPrice.toLocaleString()}
                                                </div>
                                            </div>
                                            <div className="ml-2">
                                                {isExpanded ? (
                                                    <MdExpandLess size={24} />
                                                ) : (
                                                    <MdExpandMore size={24} />
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Expanded Table */}
                                    {isExpanded && (
                                        <div className="overflow-x-auto p-4">
                                            <table className="w-full">
                                                <thead>
                                                    <tr className="border-b border-gray-200 dark:border-navy-600">
                                                        <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                                            
                                                        </th>
                                                        <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                                            Buyer
                                                        </th>
                                                        <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                                            Price per Lead
                                                        </th>
                                                        <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                                            Volume
                                                        </th>
                                                        <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                                            Total Budget
                                                        </th>
                                                        <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                                            Received
                                                        </th>
                                                        <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                                            Status
                                                        </th>
                                                        <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                                            Departments
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {group.orders
                                                        .sort((a, b) => (b.maxPricePerLead || b.price || 0) - (a.maxPricePerLead || a.price || 0))
                                                        .map((order) => {
                                                            const isOrderExpanded = expandedOrders.has(order._id);
                                                            return (
                                                                <React.Fragment key={order._id}>
                                                                    <tr className="border-b border-gray-100 dark:border-navy-700 hover:bg-gray-50 dark:hover:bg-navy-800">
                                                                        <td className="py-3">
                                                                            <button
                                                                                onClick={() => toggleOrder(order._id)}
                                                                                className="rounded p-1 hover:bg-gray-200 dark:hover:bg-navy-700"
                                                                            >
                                                                                {isOrderExpanded ? (
                                                                                    <MdExpandLess size={20} />
                                                                                ) : (
                                                                                    <MdExpandMore size={20} />
                                                                                )}
                                                                            </button>
                                                                        </td>
                                                                        <td className="py-3 text-sm">
                                                                            {order.ownerId ? (
                                                                                <>
                                                                                    <div className="font-medium">
                                                                                        {order.ownerId.firstName}{' '}
                                                                                        {order.ownerId.lastName}
                                                                                    </div>
                                                                                    <div className="text-xs text-gray-500">
                                                                                        {order.ownerId.email}
                                                                                    </div>
                                                                                </>
                                                                            ) : (
                                                                                <div className="text-gray-500">
                                                                                    Unknown buyer
                                                                                </div>
                                                                            )}
                                                                        </td>
                                                                        <td className="py-3">
                                                                            <div className="font-bold text-brand-500">
                                                                                €{(order.maxPricePerLead || order.price || 0).toFixed(2)}
                                                                            </div>
                                                                        </td>
                                                                        <td className="py-3 text-sm">
                                                                            {order.leadCount?.toLocaleString()} leads
                                                                        </td>
                                                                        <td className="py-3 text-sm font-medium">
                                                                            €{order.totalBudget?.toLocaleString()}
                                                                        </td>
                                                                        <td className="py-3 text-sm">
                                                                            <div>
                                                                                {order.leadsReceived} /{' '}
                                                                                {order.leadCount}
                                                                            </div>
                                                                            <div className="text-xs text-gray-500">
                                                                                €{order.amountSpent.toFixed(2)} spent
                                                                            </div>
                                                                        </td>
                                                                        <td className="py-3">
                                                                            <span
                                                                                className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getStatusColor(
                                                                                    order.status
                                                                                )}`}
                                                                            >
                                                                                {order.status}
                                                                            </span>
                                                                        </td>
                                                                        <td className="py-3 text-xs">
                                                                            <div className="flex flex-wrap gap-1">
                                                                                {order.departments.slice(0, 3).map((dept, i) => (
                                                                                    <span
                                                                                        key={i}
                                                                                        className="rounded bg-gray-100 px-1.5 py-0.5 dark:bg-navy-700"
                                                                                    >
                                                                                        {dept}
                                                                                    </span>
                                                                                ))}
                                                                                {order.departments.length > 3 && (
                                                                                    <span className="rounded bg-gray-100 px-1.5 py-0.5 dark:bg-navy-700">
                                                                                        +{order.departments.length - 3}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                    
                                                                    {/* Expanded Leads Table */}
                                                                    {isOrderExpanded && order.leads && order.leads.length > 0 && (
                                                                        <tr>
                                                                            <td colSpan={8} className="bg-gray-50 p-4 dark:bg-navy-900">
                                                                                <div className="mb-2 font-semibold text-navy-700 dark:text-white">
                                                                                    Leads ({order.leads.length})
                                                                                </div>
                                                                                <div className="overflow-x-auto">
                                                                                    <table className="w-full">
                                                                                        <thead>
                                                                                            <tr className="border-b border-gray-200 dark:border-navy-600">
                                                                                                <th className="pb-2 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                                                                                    Email
                                                                                                </th>
                                                                                                <th className="pb-2 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                                                                                    Seller
                                                                                                </th>
                                                                                                <th className="pb-2 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                                                                                    Seller Price
                                                                                                </th>
                                                                                                <th className="pb-2 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                                                                                    Buyer Price
                                                                                                </th>
                                                                                                <th className="pb-2 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                                                                                    Commission
                                                                                                </th>
                                                                                                <th className="pb-2 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                                                                                    Status
                                                                                                </th>
                                                                                                <th className="pb-2 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                                                                                    Paid
                                                                                                </th>
                                                                                                <th className="pb-2 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                                                                                    Date
                                                                                                </th>
                                                                                            </tr>
                                                                                        </thead>
                                                                                        <tbody>
                                                                                            {order.leads.map((lead) => {
                                                                                                const commission = (lead.buyerPrice || 0) - (lead.leadPrice || 0);
                                                                                                return (
                                                                                                    <tr
                                                                                                        key={lead._id}
                                                                                                        className="border-b border-gray-100 dark:border-navy-800"
                                                                                                    >
                                                                                                        <td className="py-2 text-xs">
                                                                                                            {lead.email || 'N/A'}
                                                                                                        </td>
                                                                                                        <td className="py-2 text-xs">
                                                                                                            {lead.sellerId ? (
                                                                                                                <>
                                                                                                                    <div className="font-medium">
                                                                                                                        {lead.sellerId.firstName} {lead.sellerId.lastName}
                                                                                                                    </div>
                                                                                                                    <div className="text-[10px] text-gray-500">
                                                                                                                        {lead.sellerId.email}
                                                                                                                    </div>
                                                                                                                </>
                                                                                                            ) : (
                                                                                                                <span className="text-gray-500">Unknown</span>
                                                                                                            )}
                                                                                                        </td>
                                                                                                        <td className="py-2 text-xs font-medium text-green-600">
                                                                                                            €{(lead.leadPrice || 0).toFixed(2)}
                                                                                                        </td>
                                                                                                        <td className="py-2 text-xs font-medium text-brand-500">
                                                                                                            €{(lead.buyerPrice || 0).toFixed(2)}
                                                                                                        </td>
                                                                                                        <td className="py-2 text-xs font-medium text-orange-500">
                                                                                                            €{commission.toFixed(2)}
                                                                                                        </td>
                                                                                                        <td className="py-2 text-xs">
                                                                                                            <span className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                                                                                                                lead.status === 'validated' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                                                                                                                lead.status === 'rejected' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                                                                                                                'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                                                                                                            }`}>
                                                                                                                {lead.status}
                                                                                                            </span>
                                                                                                        </td>
                                                                                                        <td className="py-2 text-xs">
                                                                                                            <span className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                                                                                                                lead.isPaid ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' : 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                                                                                                            }`}>
                                                                                                                {lead.isPaid ? '✓ Paid' : 'Pending'}
                                                                                                            </span>
                                                                                                        </td>
                                                                                                        <td className="py-2 text-xs text-gray-500">
                                                                                                            {new Date(lead.createdAt).toLocaleDateString()}
                                                                                                        </td>
                                                                                                    </tr>
                                                                                                );
                                                                                            })}
                                                                                        </tbody>
                                                                                    </table>
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    )}
                                                                    
                                                                    {isOrderExpanded && (!order.leads || order.leads.length === 0) && (
                                                                        <tr>
                                                                            <td colSpan={8} className="bg-gray-50 p-4 text-center text-sm text-gray-500 dark:bg-navy-900">
                                                                                No leads received yet
                                                                            </td>
                                                                        </tr>
                                                                    )}
                                                                </React.Fragment>
                                                            );
                                                        })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </Card>
        </div>
    );
}
